// Class used to validate user input on forms.

import {
  REGEX_EMAIL_ADDRESS,
  REGEX_PERSON_NAME,
  REGEX_COMPANY_NAME,
  REGEX_PHONE_NUMBER,
  REGEX_NUMERIC_ONLY,
  REGEX_ALPHA_ONLY
} from "./constants";
import type { UserInputValidationArgs } from './marops-types';

const ERROR_REQUIRED_FIELD = 'Required field empty';
const ERROR_VALUE_MIN_LENGTH = 'Must be at least 2 characters long';
const ERROR_VALUE_MAX_LENGTH = 'Exceeds maximum length';
const ERROR_INVALID_EMAIL = 'Invalid email address';
const ERROR_INVALID_COMPANY = 'Company Name may only contain letters, spaces, hyphens, and commas';
const ERROR_INVALID_PHONE = 'Please enter valid phone number';
const ERROR_ALPHA_ONLY = 'Must only contain letters';
const ERROR_NUMERIC_ONLY = 'Must only contain numbers';
const ERROR_INVALID_PATTERN = 'Must only contain letters and spaces';

class UserInputValidation {

  fieldName = '';
  userInput= '';
  rules = '';
  ruleList: Array<string> = [];
  errors: Array<string> = [];
  errorMessage = '';

  constructor(args: UserInputValidationArgs) {
    if (!args) {
      args = {
        input: '',
        rules: '',
        fieldName: '',
      }
    }
    this.userInput = args.input;
    this.rules = args.rules;
    this.errorMessage = '';
    this.ruleList = this.rules === '' ? [] : this.rules.split('|');
    this.fieldName = args.fieldName || '';
  }

  validate() {
    this.ruleList.forEach(r => {
      const activeRule = r.replace(new RegExp(/min:[0-9]+/), 'min').replace(new RegExp(/max:[0-9]+/), 'max');
      switch(activeRule) {
        case 'required':
          this._required();
          break;
        case 'firstName':
          this._firstName();
          break;
        case 'lastName':
          this._lastName();
          break;
        case 'company':
          this._company();
          break;
        case 'email':
          this._emailAddress();
          break;
        case 'phone':
          this._phoneNumber();
          break;
        case 'min':
          this._minLength(r);
          break;
        case 'max':
          this._maxLength(r);
          break;
        case 'alpha':
          this._alphaOnly();
          break;
        case 'numeric':
          this._numericOnly();
          break;
        default:
          break;
      }
    });
    return this.errors.length === 0;
  }

  validateAll() {
    console.debug('TODO: Implement this method to validate all user input');
    return true;
  }

  _required() {
    if (!this.userInput) {
      this.errorMessage = ERROR_REQUIRED_FIELD;
      this.errors.push(this.errorMessage);
    }
  }

  _firstName() {
    if (!REGEX_PERSON_NAME.test(this.userInput)) {
      if (this.userInput.length == 0) {
        this.errorMessage = ERROR_REQUIRED_FIELD;
      } else {
        if (this.userInput.length < 2) {
          this.errorMessage = ERROR_VALUE_MIN_LENGTH;
        } else {
          this.errorMessage = ERROR_INVALID_PATTERN;
        }
      }
      this.errors.push(this.errorMessage);
    }
  }

  _lastName() {
    if (!REGEX_PERSON_NAME.test(this.userInput)) {
      if (this.userInput.length == 0) {
        this.errorMessage = ERROR_REQUIRED_FIELD;
      } else {
        if (this.userInput.length < 2) {
          this.errorMessage = ERROR_VALUE_MIN_LENGTH;
        } else {
          this.errorMessage = ERROR_INVALID_PATTERN;
        }
      }
      this.errors.push(this.errorMessage);
    }
  }

  _company() {
    if (!REGEX_COMPANY_NAME.test(this.userInput)) {
      if (this.userInput.length == 0) {
        this.errorMessage = ERROR_REQUIRED_FIELD;
      } else {
        if (this.userInput.length < 2) {
          this.errorMessage = ERROR_VALUE_MIN_LENGTH;
        } else {
          this.errorMessage = ERROR_INVALID_COMPANY;
        }
      }
      this.errors.push(this.errorMessage);
    }
  }

  _emailAddress() {
    if (!REGEX_EMAIL_ADDRESS.test(this.userInput)) {
      if (this.userInput.length == 0) {
        this.errorMessage = ERROR_REQUIRED_FIELD;
      } else {
        this.errorMessage = ERROR_INVALID_EMAIL;
      }
      this.errors.push(this.errorMessage);
    }
  }

  _phoneNumber() {
    if (!REGEX_PHONE_NUMBER.test(this.userInput)) {
      if (this.userInput.length == 0) {
        this.errorMessage = ERROR_REQUIRED_FIELD;
      } else {
        this.errorMessage = ERROR_INVALID_PHONE;
      }
      this.errors.push(this.errorMessage);
    }
  }

  _minLength(rule: string) {
    const min = rule.split(':');
    if (min[1]) {
      if (this.userInput.length < parseInt(min[1])) {
        this.errorMessage = `Must be at least ${min[1]} characters long`;
        this.errors.push(this.errorMessage);
      }
    } else {
      console.error('Unable to validate minimum length.  Ruleset must match pattern min:<number>');
    }
  }

  _maxLength(rule: string) {
    const max = rule.split(':');
    if (max[1]) {
      if (this.userInput.length > parseInt(max[1])) {
        this.errorMessage = `${ERROR_VALUE_MAX_LENGTH} of ${max[1]} characters`;
        this.errors.push(this.errorMessage);
      }
    } else {
      console.error('Unable to validate maximum length.  Ruleset must match pattern max:<number>');
    }
  }

  _alphaOnly() {
    if (!REGEX_ALPHA_ONLY.test(this.userInput)) {
      this.errorMessage = ERROR_ALPHA_ONLY;
      this.errors.push(this.errorMessage);
    }
  }

  _numericOnly() {
    if (!REGEX_NUMERIC_ONLY.test(this.userInput)) {
      this.errorMessage = ERROR_NUMERIC_ONLY;
      this.errors.push(this.errorMessage);
    }
  }

}

export default UserInputValidation;
